import axios from 'axios';
import { message } from 'antd';
import { getApiBaseUrl } from '../config/api';
import { Website, CreateWebsiteDto, UpdateWebsiteDto, WebsiteFiles, FileTreeNode, GaTrafficSnapshot } from '../types/website';
import { SpamLink, CreateSpamLinkDto, UpdateSpamLinkDto, SpamLinkDomain, AddDomainsDto } from '../types/spam-link';

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration and IP whitelist issues
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token and user data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      // Check if it's an IP whitelist issue
      const errorMessage = error.response?.data?.message || '';
      console.log('API Interceptor: 403 error detected:', errorMessage);
      if (errorMessage.includes('not whitelisted') || errorMessage.includes('Access denied')) {
        // Clear user session and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Show error message before redirect
        message.error('Access denied: Your IP address is not whitelisted. You have been logged out.');
        
        // Redirect to 404 page
        if (window.location.pathname !== '/404') {
          console.log('API Interceptor: Redirecting to /404');
          window.location.href = '/404';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const websiteApi = {
  // Get all websites with pagination and filters
  getAll: async (params?: {
    page?: number;
    limit?: number;
    sslStatus?: boolean;
    hasDirLinks?: boolean;
    isGenerated?: boolean;
    isActive?: boolean;
    cloudflare_domain_onboarded?: boolean;
    cloudflare_domain_status?: string;
    status?: string;
    isIndexed?: boolean;
    search?: string;
    hasGaKey?: boolean;
  }): Promise<{ data: Website[]; total: number; page: number; limit: number; totalPages: number }> => {
    const queryParams: any = {};
    if (params?.page) queryParams.page = params.page.toString();
    if (params?.limit) queryParams.limit = params.limit.toString();
    if (params?.sslStatus !== undefined) queryParams.sslStatus = params.sslStatus.toString();
    if (params?.hasDirLinks !== undefined) queryParams.hasDirLinks = params.hasDirLinks.toString();
    if (params?.isGenerated !== undefined) queryParams.isGenerated = params.isGenerated.toString();
    if (params?.isActive !== undefined) queryParams.isActive = params.isActive.toString();
    if (params?.cloudflare_domain_onboarded !== undefined) queryParams.cloudflare_domain_onboarded = params.cloudflare_domain_onboarded.toString();
    if (params?.cloudflare_domain_status) queryParams.cloudflare_domain_status = params.cloudflare_domain_status;
    if (params?.status) queryParams.status = params.status;
    if (params?.isIndexed !== undefined) queryParams.isIndexed = params.isIndexed.toString();
    if (params?.hasGaKey !== undefined) queryParams.hasGaKey = params.hasGaKey.toString();
    if (params?.search) queryParams.search = params.search;
    
    const response = await api.get('/websites', { params: queryParams });
    return response.data;
  },

  // Get website by ID
  getById: async (id: string): Promise<Website> => {
    const response = await api.get(`/websites/${id}`);
    return response.data;
  },

  // Create new website
  create: async (data: CreateWebsiteDto): Promise<Website> => {
    const response = await api.post('/websites', data);
    return response.data;
  },

  // Update website
  update: async (id: string, data: UpdateWebsiteDto): Promise<Website> => {
    const response = await api.patch(`/websites/${id}`, data);
    return response.data;
  },

  // Delete website
  delete: async (id: string): Promise<void> => {
    await api.delete(`/websites/${id}`);
  },

  // Upload files to website
  uploadFiles: async (id: string, files: File[]): Promise<{ message: string; uploadedFiles: string[] }> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await api.post(`/websites/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get website files
  getFiles: async (id: string): Promise<WebsiteFiles> => {
    const response = await api.get(`/websites/${id}/files`);
    return response.data;
  },

  // Get website files tree structure
  getFilesTree: async (id: string): Promise<FileTreeNode> => {
    const response = await api.get(`/websites/${id}/files/tree`);
    return response.data;
  },

  // Delete file from website
  deleteFile: async (id: string, filename: string): Promise<void> => {
    await api.delete(`/websites/${id}/files/${filename}`);
  },

  // Regenerate website
  regenerateWebsite: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/website-generator/regenerate/${id}`);
    return response.data;
  },

  // Generate website
  generateWebsite: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/website-generator/generate/${id}`);
    return response.data;
  },

  // Update website menu
  updateMenu: async (id: string, menu: any[]): Promise<{ message: string }> => {
    const response = await api.patch(`/websites/${id}/menu`, { menu });
    return response.data;
  },

  // Import videos
  importVideos: async (id: string, formData: FormData): Promise<{ success: boolean; message: string; importedCount: number }> => {
    const response = await api.post(`/websites/${id}/videos/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get file content
  getFileContent: async (id: string, filePath: string): Promise<string> => {
    const response = await api.get(`/websites/${id}/files/content`, {
      params: { filePath },
    });
    return response.data.content;
  },

  getGaTraffic: async (id: string): Promise<{
    websiteId: string;
    domain: string;
    ga_tag_key?: string;
    ga_stream_id?: string;
    ga_traffic?: GaTrafficSnapshot | null;
  }> => {
    const response = await api.get(`/websites/${id}/ga/traffic`);
    return response.data;
  },

  listGaTraffic: async (params: {
    from: string;
    to: string;
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    data: Array<{
      websiteId: string;
      domain: string;
      ga_tag_key?: string;
      ga_stream_id?: string;
      users: number;
      sessions: number;
      pageviews: number;
      daily: Array<{ date: string; users: number; sessions: number; pageviews: number }>;
      periods: {
        today: { users: number; sessions: number; pageviews: number };
        last_3d: { users: number; sessions: number; pageviews: number };
        last_7d: { users: number; sessions: number; pageviews: number };
        last_30d: { users: number; sessions: number; pageviews: number };
      };
      last_synced_at?: string | null;
      sync_error?: string | null;
    }>;
    total: number;
    page: number;
    limit: number;
    from: string;
    to: string;
  }> => {
    const response = await api.get('/websites/ga/traffic', {
      params: {
        from: params.from,
        to: params.to,
        page: params.page,
        limit: params.limit,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      },
    });
    return response.data;
  },

  syncGaTraffic: async (id: string): Promise<{
    websiteId: string;
    domain: string;
    ga_tag_key?: string;
    ga_stream_id?: string;
    ga_traffic?: GaTrafficSnapshot | null;
  }> => {
    const response = await api.post(`/websites/${id}/ga/traffic/sync`);
    return response.data;
  },

  syncAllGaTraffic: async (): Promise<{
    jobId: string;
    message: string;
    status: GaTrafficSyncJobStatus;
  }> => {
    const response = await api.post('/websites/ga/traffic/sync-all');
    return response.data;
  },

  getGaTrafficSyncJob: async (jobId: string): Promise<GaTrafficSyncJobStatus> => {
    const response = await api.get(`/websites/ga/traffic/sync-all/${jobId}`);
    return response.data;
  },

  getLatestGaTrafficSyncJob: async (): Promise<GaTrafficSyncJobStatus | null> => {
    const response = await api.get('/websites/ga/traffic/sync-all/latest');
    return response.data;
  },

  exportGaTraffic: async (params: {
    from: string;
    to: string;
    search?: string;
    format: 'csv' | 'excel';
  }): Promise<Blob> => {
    const response = await api.get('/websites/ga/traffic/export', {
      params: {
        from: params.from,
        to: params.to,
        search: params.search,
        format: params.format,
      },
      responseType: 'blob',
    });
    return response.data;
  },
};

export interface GaTrafficSyncJobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total: number;
  success: number;
  failed: number;
  processed: number;
  currentDomain?: string | null;
  errors: Array<{ domain: string; error: string; timestamp?: string }>;
  startedAt?: string | null;
  completedAt?: string | null;
  errorMessage?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// Website Generator API
export const websiteGeneratorApi = {
  // Get available themes
  getThemes: async (): Promise<any[]> => {
    const response = await api.get('/themes');
    return response.data.themes;
  },

  // Generate website
  generateWebsite: async (websiteId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/website-generator/generate/${websiteId}`);
    return response.data;
  },

  // Regenerate website
  regenerateWebsite: async (websiteId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/website-generator/regenerate/${websiteId}`);
    return response.data;
  },
};

// Progress API
export const progressApi = {
  getProgress: async (websiteId: string) => {
    const response = await api.get(`/website-generator/progress/${websiteId}`);
    return response.data;
  },
};


export const spamLinkApi = {
  getAll: async (params?: { page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const response = await api.get(`/spam-links?${queryParams.toString()}`);
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/spam-links/${id}`);
    return response.data;
  },

  create: async (data: CreateSpamLinkDto): Promise<SpamLink> => {
    const response = await api.post('/spam-links', data);
    return response.data;
  },

  update: async (id: string, data: UpdateSpamLinkDto): Promise<SpamLink> => {
    const response = await api.patch(`/spam-links/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/spam-links/${id}`);
  },

  // Domain management
  addDomains: async (id: string, data: AddDomainsDto): Promise<SpamLinkDomain[]> => {
    const response = await api.post(`/spam-links/${id}/domains`, data);
    return response.data;
  },

  getDomains: async (id: string): Promise<SpamLinkDomain[]> => {
    const response = await api.get(`/spam-links/${id}/domains`);
    return response.data;
  },

  removeDomain: async (id: string, index: number, reason?: string): Promise<void> => {
    await api.delete(`/spam-links/${id}/domains/${index}`, {
      data: reason ? { reason } : undefined,
    });
  },

  updateDomainStatus: async (id: string, index: number, status: string): Promise<SpamLinkDomain> => {
    const response = await api.patch(`/spam-links/${id}/domains/${index}/status`, { status });
    return response.data;
  },

  // History
  getDomainHistory: async (id: string) => {
    const response = await api.get(`/spam-links/${id}/history`);
    return response.data;
  },
};

export default api;
